import React, { useState } from "react";
import * as XLSX from "xlsx";

const TicketList = ({ tickets, handleChat, handleCloseTicket }) => {
  const [sortedTickets, setSortedTickets] = useState(tickets);
  const [sortOrder, setSortOrder] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("");

  // 🔁 Sorting handler
  const handleSort = () => {
    const sorted = [...sortedTickets];
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";

    sorted.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return newSortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    setSortedTickets(sorted);
    setSortOrder(newSortOrder);
  };

  // ✅ Apply status filter
  const filteredTickets = sortedTickets.filter((ticket) => {
    if (statusFilter === "") return true;
    return ticket.status.toLowerCase() === statusFilter.toLowerCase();
  });

  // 🧾 Excel Download Handler
  const handleDownloadExcel = () => {
    const now = new Date();

    const formattedTimestamp = now
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/[/,]/g, "-")
      .replace(/ /g, "_")
      .replace(/:/g, "-");

    const fileName = `Support_Tickets_Report_${formattedTimestamp}.xlsx`;
    const downloadTime = now.toLocaleString("en-IN");

    const customHeader = [
      ["OrderMinds Freelancing Solutions LLP"],
      ["Support Tickets Report"],
      [`Downloaded on: ${downloadTime}`],
      [], // empty row
    ];

    const dataRows = filteredTickets.map((ticket) => ({
      "Ticket ID": ticket.ticket_id,
      "Freelancer ID": ticket.freelancer_id,
      "Freelancer Name": ticket.freelancer_name || "—",
      Subject: ticket.title,
      Description: ticket.description,
      Status: ticket.status,
      "Created At": new Date(ticket.created_at).toLocaleString("en-IN"),
    }));

    const ws = XLSX.utils.json_to_sheet(dataRows, { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, customHeader, { origin: "A1" });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SupportTickets");

    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="overflow-x-auto">
      {/* 🔽 Filter and Download Section */}
      <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
        <div>
          <label htmlFor="status-filter" className="mr-2 text-sm">
            Filter by Status:
          </label>
          <select
            id="status-filter"
            className="border px-2 py-1 rounded"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <button
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
          onClick={handleDownloadExcel}
        >
          Download Excel
        </button>
      </div>

      {/* 🧾 Tickets Table */}
      <table className="min-w-full border border-gray-300 text-sm">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="border p-3">Ticket ID</th>
            <th className="border p-3">Freelancer ID</th>
            <th className="border p-3">Freelancer Name</th> {/* ✅ NEW COLUMN */}
            <th className="border p-3">Issue</th>
            <th className="border p-3">Description</th>
            <th className="border p-3">Status</th>
            <th className="border p-3">
              <button
                onClick={handleSort}
                className="flex items-center justify-between w-full text-sm"
              >
                Created At
                {sortOrder === "asc" ? (
                  <span className="ml-1 text-xs">↑</span>
                ) : (
                  <span className="ml-1 text-xs">↓</span>
                )}
              </button>
            </th>
            <th className="border p-3">Action-1</th>
            <th className="border p-3">Action-2</th>
          </tr>
        </thead>
        <tbody>
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => (
              <tr key={ticket.ticket_id} className="border-b hover:bg-gray-50">
                <td className="border p-3">{ticket.ticket_id}</td>
                <td className="border p-3">{ticket.freelancer_id}</td>
                <td className="border p-3">{ticket.freelancer_name || "—"}</td> {/* ✅ DISPLAY NAME */}
                <td className="border p-3">{ticket.title}</td>
                <td className="border p-3">{ticket.description}</td>
                <td className="border p-3 font-bold uppercase">
                  {ticket.status}
                </td>
                <td className="border p-3">
                  {new Date(ticket.created_at).toLocaleString("en-IN")}
                </td>
                <td className="border p-3">
                  <button
                    className="text-blue-600 hover:underline"
                    onClick={() => handleChat(ticket.ticket_id)}
                  >
                    Chat
                  </button>
                </td>
                <td className="border p-3">
                  {ticket.status !== "closed" ? (
                    <button
                      className="text-red-600 hover:underline"
                      onClick={() => handleCloseTicket(ticket.ticket_id)}
                    >
                      Close Ticket
                    </button>
                  ) : (
                    <span className="text-gray-500">Already Closed</span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center p-4">
                No Tickets Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TicketList;
